import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import {
  ProfileSettingsModal,
  Form,
  FieldTextInput,
  FieldSelectModern,
  InlineTextButton,
} from '../../components';

import css from './RequestWorkExperienceTestimonialForm.css';

const SOURCE_WORK_EXPERIENCE = 'workExperience';
const SOURCE_WORKED_WITH = 'workedWith';

const stripHtml = html =>
  (html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const truncate = (text, max = 60) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

const buildOptions = (items, source) => {
  const list = items || [];
  const nameCounts = list.reduce((acc, item) => {
    const name = item.customerName || '';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  return list.map(item => {
    const name = item.customerName || '';
    const taskText = truncate(stripHtml(item.task));
    const showTask = nameCounts[name] > 1 && taskText;
    return {
      value: `${source}:${item.id}`,
      label: showTask ? `${name} — ${taskText}` : name,
      key: `${source}:${item.id}`,
      data: { ...item, source },
    };
  });
};

const RequestWorkExperienceTestimonialForm = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmit,
    workExperiences,
    pastClients,
    preselectedItem,
    freelancerName,
    updateInProgress,
    intl,
  } = props;

  const [step, setStep] = useState('form');

  const workExperienceOptions = useMemo(
    () => buildOptions(workExperiences, SOURCE_WORK_EXPERIENCE),
    [workExperiences]
  );
  const pastClientOptions = useMemo(
    () => buildOptions(pastClients, SOURCE_WORKED_WITH),
    [pastClients]
  );

  const groupedOptions = useMemo(() => {
    const groups = [];
    if (workExperienceOptions.length > 0) {
      groups.push({
        label: intl.formatMessage({
          id: 'RequestWorkExperienceTestimonialForm.clientGroupWorkExperience',
        }),
        options: workExperienceOptions,
      });
    }
    if (pastClientOptions.length > 0) {
      groups.push({
        label: intl.formatMessage({
          id: 'RequestWorkExperienceTestimonialForm.clientGroupPastClients',
        }),
        options: pastClientOptions,
      });
    }
    return groups;
  }, [workExperienceOptions, pastClientOptions, intl]);

  const initialClientOption = useMemo(() => {
    if (!preselectedItem) return null;
    const pool =
      preselectedItem.source === SOURCE_WORK_EXPERIENCE ? workExperienceOptions : pastClientOptions;
    return pool.find(o => o.data.id === preselectedItem.id) || null;
  }, [preselectedItem, workExperienceOptions, pastClientOptions]);

  // Reset to form step whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      setStep('form');
    }
  }, [isOpen]);

  const labels = {
    title: intl.formatMessage({ id: 'RequestWorkExperienceTestimonialForm.title' }),
    instructions1: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.requestInstructions1',
    }),
    instructions2: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.requestInstructions2',
    }),
    instructions3: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.requestInstructions3',
    }),
    clientLabel: intl.formatMessage({ id: 'RequestWorkExperienceTestimonialForm.clientLabel' }),
    clientPlaceholder: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.clientPlaceholder',
    }),
    clientNoOptions: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.clientNoOptions',
    }),
    taskLabel: intl.formatMessage({ id: 'RequestWorkExperienceTestimonialForm.taskLabel' }),
    descriptionLabel: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.descriptionLabel',
    }),
    authorNameLabel: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.authorNameLabel',
    }),
    authorNamePlaceholder: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.authorNamePlaceholder',
    }),
    authorEmailLabel: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.authorEmailLabel',
    }),
    authorEmailPlaceholder: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.authorEmailPlaceholder',
    }),
    authorEmailInvalid: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.authorEmailInvalid',
    }),
    previewButton: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.previewButton',
    }),
    backButton: intl.formatMessage({ id: 'RequestWorkExperienceTestimonialForm.backButton' }),
    sendButton: intl.formatMessage({ id: 'RequestWorkExperienceTestimonialForm.sendButton' }),
    previewIntro1: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.previewIntro1',
    }),
    previewIntro2: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.previewIntro2',
    }),
    emailToLabel: intl.formatMessage({ id: 'RequestWorkExperienceTestimonialForm.emailToLabel' }),
    emailFromLabel: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.emailFromLabel',
    }),
    emailFromValue: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.emailFromValue',
    }),
    emailSubjectLabel: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.emailSubjectLabel',
    }),
    emailLinkLabel: intl.formatMessage({
      id: 'RequestWorkExperienceTestimonialForm.emailLinkLabel',
    }),
    emailSignoff: intl.formatMessage({ id: 'RequestWorkExperienceTestimonialForm.emailSignoff' }),
    emailTeam: intl.formatMessage({ id: 'RequestWorkExperienceTestimonialForm.emailTeam' }),
  };

  const emailValid = validators.emailFormatValid(labels.authorEmailInvalid);

  return (
    <FinalForm
      onSubmit={values => {
        const selected = values.client?.data;
        if (!selected) return;
        onSubmit({
          authorEmail: values.authorEmail,
          authorName: values.authorName,
          workExperienceId: selected.id,
        });
      }}
      initialValues={{
        client: initialClientOption,
        authorName: '',
        authorEmail: '',
      }}
      render={({ handleSubmit, values, valid }) => {
        const selected = values.client?.data || null;
        const taskText = selected?.task || null;
        const descriptionText = selected?.description || null;

        const previewDisabled =
          !values.client || !values.authorName || !values.authorEmail || !valid;

        const authorFirstName = (values.authorName || '').trim().split(/\s+/)[0] || '';

        const submitLabel = step === 'form' ? labels.previewButton : labels.sendButton;
        const submitDisabled = step === 'form' ? previewDisabled : false;
        const handleModalSubmit =
          step === 'form'
            ? () => {
                if (!previewDisabled) setStep('preview');
              }
            : handleSubmit;

        return (
          <ProfileSettingsModal
            id="requestTestimonial"
            isOpen={isOpen}
            onClose={onClose}
            onManageDisableScrolling={onManageDisableScrolling}
            title={labels.title}
            onSubmit={handleModalSubmit}
            submitDisabled={submitDisabled}
            submitInProgress={updateInProgress}
            submitLabel={submitLabel}
          >
            {step === 'form' ? (
              <Form className={css.formContent} onSubmit={() => {}}>
                <div className={css.instructions}>
                  <p>{labels.instructions1}</p>
                  <p>{labels.instructions2}</p>
                  <p>{labels.instructions3}</p>
                </div>

                <FieldSelectModern
                  id="client"
                  name="client"
                  label={labels.clientLabel}
                  placeholder={labels.clientPlaceholder}
                  options={groupedOptions}
                  noOptionsMessage={() => labels.clientNoOptions}
                />

                {selected && (taskText || descriptionText) ? (
                  <div className={css.previewBlock}>
                    {taskText ? (
                      <div className={css.previewRow}>
                        <span className={css.previewRowLabel}>{labels.taskLabel}</span>
                        <span
                          className={css.previewRowValue}
                          dangerouslySetInnerHTML={{ __html: taskText }}
                        />
                      </div>
                    ) : null}
                    {descriptionText ? (
                      <div className={css.previewRow}>
                        <span className={css.previewRowLabel}>{labels.descriptionLabel}</span>
                        <span
                          className={`${css.previewRowValue} ${css.previewRowValueClamped}`}
                          dangerouslySetInnerHTML={{ __html: descriptionText }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <FieldTextInput
                  id="authorName"
                  name="authorName"
                  type="text"
                  label={labels.authorNameLabel}
                  placeholder={labels.authorNamePlaceholder}
                />
                <FieldTextInput
                  id="authorEmail"
                  name="authorEmail"
                  type="text"
                  label={labels.authorEmailLabel}
                  placeholder={labels.authorEmailPlaceholder}
                  validate={emailValid}
                />
              </Form>
            ) : (
              <div className={css.previewWrapper}>
                <InlineTextButton
                  type="button"
                  className={css.backLink}
                  onClick={() => setStep('form')}
                >
                  ← {labels.backButton}
                </InlineTextButton>

                <div className={css.instructions}>
                  <p>{labels.previewIntro1}</p>
                  <p>{labels.previewIntro2}</p>
                </div>

                <div className={css.emailCard}>
                  <div className={css.emailRow}>
                    <span className={css.emailRowLabel}>{labels.emailToLabel}</span>
                    <span className={css.emailRowValue}>{values.authorEmail}</span>
                  </div>
                  <div className={css.emailRow}>
                    <span className={css.emailRowLabel}>{labels.emailFromLabel}</span>
                    <span className={css.emailRowValue}>{labels.emailFromValue}</span>
                  </div>
                  <div className={css.emailRow}>
                    <span className={css.emailRowLabel}>{labels.emailSubjectLabel}</span>
                    <span className={css.emailRowValue}>
                      <FormattedMessage
                        id="RequestWorkExperienceTestimonialForm.emailSubject"
                        values={{ freelancerName }}
                      />
                    </span>
                  </div>

                  <div className={css.emailBody}>
                    <p>
                      <FormattedMessage
                        id="RequestWorkExperienceTestimonialForm.emailGreeting"
                        values={{ authorFirstName }}
                      />
                    </p>
                    <p>
                      <FormattedMessage
                        id="RequestWorkExperienceTestimonialForm.emailBody"
                        values={{ freelancerName }}
                      />
                    </p>
                    <p>
                      <span className={css.emailLink}>{labels.emailLinkLabel}</span>
                    </p>
                    <p>{labels.emailSignoff}</p>
                    <p>{labels.emailTeam}</p>
                  </div>
                </div>
              </div>
            )}
          </ProfileSettingsModal>
        );
      }}
    />
  );
};

RequestWorkExperienceTestimonialForm.defaultProps = {
  workExperiences: [],
  pastClients: [],
  preselectedItem: null,
  freelancerName: '',
  updateInProgress: false,
};

RequestWorkExperienceTestimonialForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  workExperiences: PropTypes.array,
  pastClients: PropTypes.array,
  preselectedItem: PropTypes.object,
  freelancerName: PropTypes.string,
  updateInProgress: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(RequestWorkExperienceTestimonialForm);
